import { Check, CircleCheck, Clock3, HandCoins, Loader2, Undo2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { InfoNote } from '@/components/common/Panel'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, promptModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDateTime, fmtMoney } from '../shared/format'
import { Section } from './components/FinanceBits'
import {
  useApprovePayment,
  useCancelPayment,
  usePaymentApprovalSettings,
  usePaymentRequests,
  useRejectPayment,
  useSavePaymentThreshold,
  type PaymentRequest,
  type PaymentRequestStatus,
} from './paymentRequests'
import { FINANCE_PAYMENT_REQUEST_SETTINGS } from './permissions'
import { toastError } from './ui'

const FILTERS = ['pending', 'approved', 'rejected', 'all'] as const
type Filter = (typeof FILTERS)[number]
const STATUS_TONE: Record<PaymentRequestStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
  failed: 'danger',
}

/**
 * Ödeme onayları: personelin onaya gönderdiği çıkan ödemeler. Firma sahibi ve ortaklar onaylar (eşik altı biri, eşik ve
 * üstü hepsi); son onayla ödeme işlenir. Talep eden bekleyen talebi geri çekebilir; işlenen ödeme iptal akışıyla iptal edilir.
 */
export function PaymentRequestsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [filter, setFilter] = useState<Filter>('pending')
  const list = usePaymentRequests(filter === 'all' ? '' : filter)
  const approve = useApprovePayment()
  const reject = useRejectPayment()
  const cancel = useCancelPayment()

  const onApprove = async (r: PaymentRequest) => {
    const last = r.level === 'single' || r.approvers.filter((a) => !a.approved).length === 1
    const ok = await confirmModal({
      title: t('finance.paymentRequests.approveTitle'),
      description: t(last ? 'finance.paymentRequests.approveBodyLast' : 'finance.paymentRequests.approveBody', {
        summary: r.summary,
        amount: fmtMoney(r.amount),
      }),
      confirmLabel: t('finance.paymentRequests.approve'),
    })
    if (ok)
      approve.mutate(r.id, {
        onSuccess: () =>
          toast.success(t(last ? 'finance.paymentRequests.executed' : 'finance.paymentRequests.approved')),
        onError: toastError,
      })
  }
  const onReject = async (r: PaymentRequest) => {
    const note = await promptModal({
      title: t('finance.paymentRequests.rejectTitle'),
      placeholder: t('finance.paymentRequests.rejectPlaceholder'),
      confirmLabel: t('finance.paymentRequests.reject'),
    })
    if (note == null) return
    if (!note.trim()) {
      toast.error(t('finance.paymentRequests.rejectNoteRequired'))
      return
    }
    reject.mutate(
      { id: r.id, note: note.trim() },
      { onSuccess: () => toast.success(t('finance.paymentRequests.rejected')), onError: toastError },
    )
  }
  const onCancel = async (r: PaymentRequest) => {
    const ok = await confirmModal({
      title: t('finance.paymentRequests.withdrawTitle'),
      description: r.summary,
      confirmLabel: t('finance.paymentRequests.withdraw'),
      destructive: true,
    })
    if (ok)
      cancel.mutate(r.id, {
        onSuccess: () => toast.success(t('finance.paymentRequests.withdrawn')),
        onError: toastError,
      })
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.paymentRequests.title')}
        description={t('finance.paymentRequests.subtitle')}
      />
      <div className="space-y-4">
        <ThresholdCard editable={can(FINANCE_PAYMENT_REQUEST_SETTINGS)} />
        <Section
          title={t('finance.paymentRequests.listTitle')}
          bodyClassName="p-0"
          actions={
            <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <TabsList size="sm">
                {FILTERS.map((f) => (
                  <TabsTrigger key={f} value={f}>
                    {t(`finance.paymentRequests.filter.${f}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          }
        >
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : list.isLoading ? (
            <Skeleton className="m-4 h-40" />
          ) : !list.data?.length ? (
            <EmptyState icon={HandCoins} title={t('finance.paymentRequests.empty')} />
          ) : (
            <ul className="divide-y">
              {list.data.map((r) => (
                <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-0 flex-1 basis-64 space-y-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      <span className="truncate">{r.summary}</span>
                      <Badge tone="neutral">{t(`finance.paymentRequests.kind.${r.kind}`)}</Badge>
                      <Badge tone={r.level === 'all' ? 'info' : 'primary'}>
                        {t(`finance.paymentRequests.level.${r.level}`)}
                      </Badge>
                      <Badge tone={STATUS_TONE[r.status]}>{t(`finance.paymentRequests.status.${r.status}`)}</Badge>
                    </p>
                    {r.reason ? <p className="text-muted-foreground text-sm">{r.reason}</p> : null}
                    {r.approvers.length ? (
                      <ul className="flex flex-wrap gap-1.5" aria-label={t('finance.paymentRequests.approvers')}>
                        {r.approvers.map((a) => (
                          <li key={a.userId}>
                            <Badge tone={a.approved ? 'success' : 'outline'}>
                              {a.approved ? (
                                <CircleCheck className="size-3" aria-hidden />
                              ) : (
                                <Clock3 className="size-3" aria-hidden />
                              )}
                              {a.name || '—'}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <p className="text-muted-foreground text-xs">
                      {t('finance.paymentRequests.requestedBy', {
                        name: r.requestedByName || '—',
                        at: fmtDateTime(r.createdAt),
                      })}
                      {r.decidedAt
                        ? ` · ${t('finance.paymentRequests.decidedBy', { name: r.decidedByName || '—', at: fmtDateTime(r.decidedAt) })}`
                        : ''}
                    </p>
                    {r.decisionNote ? (
                      <p className="text-muted-foreground text-xs">
                        {t('finance.paymentRequests.decisionNote')}: {r.decisionNote}
                      </p>
                    ) : null}
                  </div>
                  <span className="w-32 text-right font-semibold tabular-nums">{fmtMoney(r.amount)}</span>
                  <div className="flex flex-wrap gap-2">
                    {r.canApprove ? (
                      <Button size="sm" onClick={() => void onApprove(r)} disabled={approve.isPending}>
                        <Check />
                        {t('finance.paymentRequests.approve')}
                      </Button>
                    ) : null}
                    {r.canReject ? (
                      <Button size="sm" variant="outline" onClick={() => void onReject(r)} disabled={reject.isPending}>
                        <X />
                        {t('finance.paymentRequests.reject')}
                      </Button>
                    ) : null}
                    {r.canCancel ? (
                      <Button size="sm" variant="ghost" onClick={() => void onCancel(r)} disabled={cancel.isPending}>
                        <Undo2 />
                        {t('finance.paymentRequests.withdraw')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  )
}

function ThresholdCard({ editable }: { editable: boolean }) {
  const { t } = useTranslation()
  const settings = usePaymentApprovalSettings()
  const save = useSavePaymentThreshold()
  const [value, setValue] = useState<string | null>(null)
  const current = settings.data?.threshold ?? ''
  const shown = value ?? current
  return (
    <Section title={t('finance.paymentRequests.thresholdTitle')} icon={HandCoins}>
      <p className="text-muted-foreground mb-3 text-sm">{t('finance.paymentRequests.thresholdHint')}</p>
      {settings.data && settings.data.approvers === 0 ? (
        <InfoNote>{t('finance.paymentRequests.noApprovers')}</InfoNote>
      ) : null}
      {editable ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="pay-threshold">{t('finance.paymentRequests.threshold')}</Label>
            <Input
              id="pay-threshold"
              inputMode="decimal"
              className="w-48"
              value={shown}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            disabled={value == null || value === current || !/^\d+(\.\d{1,2})?$/.test(shown) || save.isPending}
            onClick={() =>
              save.mutate(shown, {
                onSuccess: () => {
                  setValue(null)
                  toast.success(t('finance.paymentRequests.thresholdSaved'))
                },
                onError: toastError,
              })
            }
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      ) : (
        <p className="mt-3 text-lg font-semibold tabular-nums">{current ? fmtMoney(current) : '—'}</p>
      )}
    </Section>
  )
}
